import { useState, useRef, useEffect, useCallback } from "react"
import { Box, VStack, Flex, Input } from "@chakra-ui/react"
import { Send } from "lucide-react"
import { IconButton } from "@chakra-ui/react"
import type { ChallengeScript, ChatRole, AssistantActionStatus } from "@zrt-ai-office/types/course"
import { GameChatMessage } from "./game-chat-message"

interface GameChatProps {
  challengeScript: ChallengeScript
  onComplete: () => void
}

interface DisplayMessage {
  id: string
  role: ChatRole
  content: string
  actionStatus?: AssistantActionStatus
  actionLabel?: string
  isTyping?: boolean
}

export function GameChat({ challengeScript, onComplete }: GameChatProps) {
  const [messages, setMessages] = useState<DisplayMessage[]>([
    {
      id: "init",
      role: "assistant",
      content: challengeScript.initialMessage,
    },
  ])
  const [scriptIndex, setScriptIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [userCanSend, setUserCanSend] = useState(true)
  const [inputValue, setInputValue] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* ── Auto-scroll to bottom ── */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  /* ── Cleanup timeouts on unmount ── */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  /* ── Play script messages sequentially ── */
  const playNextMessage = useCallback(
    (index: number) => {
      const scriptMessages = challengeScript.messages
      if (index >= scriptMessages.length) {
        setIsPlaying(false)
        return
      }

      const msg = scriptMessages[index]

      // Show typing indicator first
      const typingId = `typing-${index}`
      setMessages((prev) => [
        ...prev,
        {
          id: typingId,
          role: msg.role,
          content: "",
          actionStatus: msg.actionStatus,
          actionLabel: msg.actionLabel,
          isTyping: true,
        },
      ])

      // After delay, replace typing with actual message
      timeoutRef.current = setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === typingId
              ? {
                  ...m,
                  id: msg.id,
                  content: msg.content,
                  isTyping: false,
                }
              : m
          )
        )

        // Check if this is a judge message (challenge complete)
        if (msg.role === "judge") {
          setIsPlaying(false)
          timeoutRef.current = setTimeout(() => {
            onComplete()
          }, 1000)
          return
        }

        // Continue to next message
        const nextIndex = index + 1
        setScriptIndex(nextIndex)
        playNextMessage(nextIndex)
      }, msg.delay)
    },
    [challengeScript.messages, onComplete]
  )

  /* ── Handle user send ── */
  const handleSend = useCallback(() => {
    const text = inputValue.trim()
    if (!text || !userCanSend || isPlaying) return

    // Add user message
    const userMsg: DisplayMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: text,
    }
    setMessages((prev) => [...prev, userMsg])
    setInputValue("")
    setUserCanSend(false)
    setIsPlaying(true)

    // Start playing script after a short delay
    timeoutRef.current = setTimeout(() => {
      playNextMessage(scriptIndex)
    }, 500)
  }, [inputValue, userCanSend, isPlaying, scriptIndex, playNextMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  return (
    <Box>
      {/* Message list */}
      <Box
        h="400px"
        overflowY="auto"
        px="4"
        py="3"
        bg="white"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.200"
        mb="3"
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-gray-300)",
            borderRadius: "2px",
          },
        }}
      >
        <VStack gap="0" align="stretch">
          {messages.map((msg) => (
            <GameChatMessage
              key={msg.id}
              role={msg.role}
              content={msg.content}
              actionStatus={msg.actionStatus}
              actionLabel={msg.actionLabel}
              isTyping={msg.isTyping}
            />
          ))}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input area */}
      <Flex gap="2">
        <Input
          flex="1"
          placeholder={
            isPlaying
              ? "等待脚本播放完成..."
              : userCanSend
                ? "输入任意消息开始闯关..."
                : "闯关进行中..."
          }
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isPlaying || !userCanSend}
          bg="white"
          borderRadius="xl"
          borderColor="gray.200"
          _focus={{ borderColor: "emerald.400", boxShadow: "0 0 0 1px var(--chakra-colors-emerald-400)" }}
          size="md"
        />
        <IconButton
          aria-label="发送消息"
          onClick={handleSend}
          disabled={isPlaying || !userCanSend || !inputValue.trim()}
          bg="emerald.500"
          color="white"
          borderRadius="xl"
          size="md"
          _hover={{ bg: "emerald.600" }}
          _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
        >
          <Send size={18} />
        </IconButton>
      </Flex>
    </Box>
  )
}
